/**
 * Embeddable chat widget. Entry point exposed as `window.AmaiChat` via
 * Vite's IIFE library build.
 *
 *   <script src="https://<host>/widget/widget.js"></script>
 *   <script>AmaiChat.init({ token: "...", baseUrl: "https://<host>" });</script>
 *
 * That shows the floating bubble. A chat window inside the page as well
 * (embed.ts) — the same chat, the bubble stays:
 *
 *   <div data-amai-chat></div>                     with the snippet above
 *   AmaiChat.init({ token, baseUrl, container: "#chat" })
 *   AmaiChat.init({ token, baseUrl }).mount(element)   (pages that render
 *                                                       it later; returns
 *                                                       the unmount)
 *
 * A `data-amai-chat` element a single-page app renders later gets its
 * window too, and the window goes when the element leaves the page.
 * The window is compact until the visitor's chat begins; the element's
 * data-amai-chat-state ("idle" | "active") and an `amai-chat:state`
 * event tell the page which (embed.ts).
 *
 * Calling `init` twice is a no-op (the second call returns the same
 * instance; a `container` it carries is mounted), also when the page
 * loads the script twice. Calling `destroy()` tears down DOM and SSE.
 */

import { Conversation } from "./conversation";
import { EmbedMount, mountEmbed } from "./embed";
import { mountLauncher } from "./launcher";
import { uiStrings } from "./pane";
import { fetchWidgetConfig, WidgetConfig } from "./remote_config";
import { ChatSession } from "./session";
import {
    clearSourceId,
    getOrCreateSourceId,
    hasBegunChat,
    rememberChatBegun,
} from "./storage";

/** The page elements that get a chat window without any script. */
const EMBED_SELECTOR = "[data-amai-chat]";

export interface InitOptions {
    token: string;
    /** Origin of the services backend, e.g. "https://staging.amaiapp.com".
     * Defaults to the same origin as the <script src=>. */
    baseUrl?: string;
    /** Override the auto-detected initial locale. */
    locale?: string;
    /** Also show the chat as a window inside this element (CSS selector
     *  or element). Elements with `data-amai-chat` get one anyway. */
    container?: string | HTMLElement;
}

export interface AmaiChatInstance {
    open: () => void;
    close: () => void;
    destroy: () => void;
    /**
     * Forget this visitor — clears the localStorage source_id, tears down
     * the running widget, and re-initializes with a fresh identity. Useful
     * for testing. The prior conversation stays in the backend Mongo as
     * orphaned data (not deleted server-side; ask the visitor to leave a
     * GDPR request if you need full removal).
     */
    reset: () => void;
    /**
     * Shows the chat as a window inside `container` (CSS selector or
     * element), for pages that render it after init — a single-page app
     * navigating to it. Returns the function that removes it again.
     */
    mount: (container: string | HTMLElement) => () => void;
}

/**
 * Where the page's running widget is kept: on window, not in this
 * module. A page that loads the script twice (a window's embed code
 * pasted on a site that already has the chat) runs a second copy of the
 * module, which must find the first one's chat rather than start a
 * second bubble, window and stream.
 */
const RUNNING = "__amaiChat";

type WidgetWindow = Window & { [RUNNING]?: AmaiChatInstance };

function running(): AmaiChatInstance | undefined {
    return (window as WidgetWindow)[RUNNING];
}

export function init(options: InitOptions): AmaiChatInstance {
    const current = running();
    if (current) {
        if (options.container) {
            const host = resolveContainer(options.container);
            if (host) current.mount(host);
            else warnNoContainer();
        }
        return current;
    }
    if (!options.token) {
        throw new Error("AmaiChat.init: `token` is required");
    }
    const initAt = Date.now();

    const baseUrl = (
        options.baseUrl ??
        deriveScriptOrigin() ??
        window.location.origin
    ).replace(/\/$/, "");

    const sourceId = getOrCreateSourceId();
    // Locale detection order (first nl/fr/en hit wins):
    //   1. Explicit AmaiChat.init({ locale: ... }) override.
    //   2. Host page <html lang="..."> — strongest organic signal, since
    //      it reflects what the visitor is currently reading.
    //   3. Browser preference (navigator.language).
    //   4. The agency default returned by /v1/api/chat/config takes
    //      effect later if all three above were undefined.
    const locale =
        normaliseLocale(options.locale) ??
        normaliseLocale(
            typeof document !== "undefined"
                ? document.documentElement.lang
                : undefined,
        ) ??
        normaliseLocale(
            typeof navigator !== "undefined" ? navigator.language : undefined,
        );

    let conversation: Conversation | undefined;
    let mounted: { open: () => void; close: () => void; destroy: () => void } | undefined;
    /** The page's chat windows: mounted, or (null) waiting for the config. */
    const embeds = new Map<HTMLElement, EmbedMount | null>();
    /** Those of them the page marked with `data-amai-chat`: they go when
     *  their element leaves the page. */
    const marked = new Set<HTMLElement>();
    /** Set once the config is in: what a chat window needs. */
    let mountWindow: ((host: HTMLElement) => EmbedMount) | undefined;
    // destroy() / reset() can run while the config request is still out;
    // without this the widget would mount afterwards anyway (and start
    // its teaser timer), with nothing left to tear it down.
    let destroyed = false;

    const mountInto = (host: HTMLElement): void => {
        if (embeds.get(host)) return;
        embeds.set(host, mountWindow ? mountWindow(host) : null);
    };
    const unmount = (host: HTMLElement): void => {
        embeds.get(host)?.destroy();
        embeds.delete(host);
        marked.delete(host);
    };
    const mountMarkedElements = (): void => {
        document
            .querySelectorAll<HTMLElement>(EMBED_SELECTOR)
            .forEach((host) => {
                marked.add(host);
                mountInto(host);
            });
    };
    // The `container` option and the page's own `data-amai-chat`
    // elements: those there when the config arrives, and those parsed
    // after it if the page was still loading.
    const mountMarked = (): void => {
        if (destroyed || !mountWindow) return;
        if (options.container) {
            const host = resolveContainer(options.container);
            if (host) mountInto(host);
            else if (document.readyState !== "loading") warnNoContainer();
        }
        mountMarkedElements();
    };
    // And the elements that come and go after that: a single-page app
    // renders the next page without loading the script again.
    const observer =
        typeof MutationObserver === "function"
            ? new MutationObserver((records) => {
                  if (destroyed || !mountWindow) return;
                  for (const host of marked) {
                      if (!host.isConnected) unmount(host);
                  }
                  if (records.some(addsMarked)) mountMarkedElements();
              })
            : undefined;

    void (async () => {
        let cfg: WidgetConfig;
        try {
            cfg = await fetchWidgetConfig(baseUrl, options.token);
        } catch (err) {
            console.warn("AmaiChat: failed to load widget config", err);
            return;
        }
        if (destroyed) return;
        const session = new ChatSession({
            baseUrl,
            widgetToken: options.token,
            sourceId,
            locale,
        });
        // Falls back to the agency default if all browser/page
        // signals were inconclusive.
        const uiLocale = locale ?? cfg.locale_default ?? "nl";
        const chat = new Conversation({
            session,
            config: cfg,
            ui: uiStrings(uiLocale),
            begunBefore: hasBegunChat(sourceId),
        });
        chat.onBegunChange((begun) => rememberChatBegun(sourceId, begun));
        conversation = chat;
        mounted = mountLauncher({
            config: cfg,
            conversation: chat,
            session,
            baseUrl,
            widgetToken: options.token,
            locale: uiLocale,
            initAt,
        });
        mountWindow = (host) =>
            mountEmbed({
                host,
                config: cfg,
                conversation: chat,
                baseUrl,
                widgetToken: options.token,
                locale: uiLocale,
            });
        for (const [host, embed] of embeds) {
            if (!embed) embeds.set(host, mountWindow(host));
        }
        mountMarked();
        if (document.readyState === "loading") {
            document.addEventListener("DOMContentLoaded", mountMarked, {
                once: true,
            });
        }
        observer?.observe(document.documentElement, {
            childList: true,
            subtree: true,
        });
    })();

    const teardown = (): void => {
        destroyed = true;
        observer?.disconnect();
        document.removeEventListener("DOMContentLoaded", mountMarked);
        for (const embed of embeds.values()) embed?.destroy();
        embeds.clear();
        marked.clear();
        mounted?.destroy();
        // Also stops a history or session request still out from
        // opening a stream once it returns.
        conversation?.close();
        if (running() === instance) delete (window as WidgetWindow)[RUNNING];
    };

    const instance: AmaiChatInstance = {
        open: () => mounted?.open(),
        close: () => mounted?.close(),
        destroy: teardown,
        reset: () => {
            // Windows the page asked for keep their place.
            const hosts = Array.from(embeds.keys());
            teardown();
            clearSourceId();
            const fresh = init(options);
            for (const host of hosts) {
                if (host.isConnected) fresh.mount(host);
            }
        },
        mount: (container) => {
            const host = resolveContainer(container);
            if (!host) {
                throw new Error(
                    "AmaiChat.mount: `container` did not resolve to an element",
                );
            }
            if (!destroyed) mountInto(host);
            return () => unmount(host);
        },
    };
    (window as WidgetWindow)[RUNNING] = instance;
    return instance;
}

/** `record` put an element with `data-amai-chat` in the page. */
function addsMarked(record: MutationRecord): boolean {
    for (const node of Array.from(record.addedNodes)) {
        if (
            node instanceof Element &&
            (node.matches(EMBED_SELECTOR) || node.querySelector(EMBED_SELECTOR))
        ) {
            return true;
        }
    }
    return false;
}

function warnNoContainer(): void {
    console.warn("AmaiChat.init: `container` did not resolve to an element");
}

function resolveContainer(c: string | HTMLElement): HTMLElement | null {
    if (typeof c !== "string") return c;
    return document.querySelector<HTMLElement>(c);
}

function normaliseLocale(v: string | undefined | null): string | undefined {
    if (!v || typeof v !== "string") return undefined;
    const head = v.slice(0, 2).toLowerCase();
    if (head === "nl" || head === "fr" || head === "en") return head;
    return undefined;
}

function deriveScriptOrigin(): string | undefined {
    const scripts = document.querySelectorAll<HTMLScriptElement>(
        "script[src*='widget.js' i]",
    );
    const last = scripts[scripts.length - 1];
    if (!last) return undefined;
    try {
        return new URL(last.src).origin;
    } catch {
        return undefined;
    }
}
